import React from 'react';
import { motion } from 'framer-motion';
import { Users, Award, Lightbulb, Activity, Eye } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Icon3DPartnership, Icon3DInnovation } from '../components/3DIcon';

const About = () => {
  return (
    <div className="bg-white dark:bg-[#001226] transition-colors duration-500">
      {/* Hero Section */}
      <section className="bg-[#002a5c] text-white py-16 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div 
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              className="max-w-3xl"
            >
              <h1 className="text-4xl md:text-5xl font-semibold mb-6">Empowering Businesses with Scalable IT Solutions</h1>
              <p className="text-blue-100 text-lg mb-8 leading-relaxed">
                OETechs is a leading digital solutions provider, committed to delivering high-performance Web, Mobile, and Digital Marketing solutions that help businesses scale faster and smarter.
              </p>
              <div className="flex flex-wrap gap-4">
                <div className="bg-white/10 backdrop-blur-md px-6 py-3 rounded-2xl border border-white/20">
                  <span className="text-2xl font-semibold text-[#002a5c]">13+</span>
                  <p className="text-xs uppercase tracking-widest font-semibold opacity-80">Years of Excellence</p>
                </div>
                <div className="bg-white/10 backdrop-blur-md px-6 py-3 rounded-2xl border border-white/20">
                  <span className="text-2xl font-semibold text-[#002a5c]">300+</span>
                  <p className="text-xs uppercase tracking-widest font-semibold opacity-80">Clients Worldwide</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.8 }}
              className="relative hidden lg:block"
            >
              <div className="relative w-full h-[350px] flex items-center justify-center">
                {/* Center Image - Replaced background rings with v.png */}
                <motion.div 
                  animate={{ 
                    y: [0, -10, 0],
                  }}
                  transition={{ 
                    duration: 4, 
                    repeat: Infinity, 
                    ease: "easeInOut"
                  }}
                  className="relative z-10 w-full max-w-[400px]"
                >
                  <img src="/images/v.png" alt="OETechs Innovation" className="w-full h-auto drop-shadow-2xl" />
                </motion.div>
                
                {/* Subtle Glow */}
                <div className="absolute inset-0 bg-blue-500/10 blur-[80px] rounded-full" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-blue-600 font-semibold uppercase tracking-widest text-sm mb-4 block">Our Story</span>
              <h2 className="text-3xl md:text-4xl font-semibold text-[#002a5c] dark:text-white mb-6">Built on Honesty, Hard Work, and Trust</h2>
              <div className="space-y-4 text-slate-600 dark:text-slate-400 leading-relaxed">
                <p>
                  OETechs was launched by aspiring entrepreneurs who saw the potential in the digital breakthrough. With over thirteen years of consistent experience in the software industry, we have evolved from a small team into a comprehensive digital ally for businesses of all sizes.
                </p>
                <p>
                  Our mission is simple: to remove the unnecessary complexities in technology and deliver lean, efficient, and high-impact solutions that drive real business results.
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-8 mt-10">
                <motion.div 
                  whileHover={{ x: 5, y: -2 }}
                  className="flex items-center gap-4 p-5 bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl border border-blue-300 hover:shadow-lg transition-all"
                >
                  <div className="w-14 h-14 bg-white text-blue-700 rounded-2xl flex items-center justify-center shrink-0 shadow-md border border-blue-300/30">
                    <Icon3DInnovation size="w-7 h-7" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-blue-700 dark:text-white text-sm">Vision</h4>
                    <p className="text-xs text-slate-600 dark:text-slate-400">Lead in innovative IT services globally.</p>
                  </div>
                </motion.div>
                <motion.div 
                  whileHover={{ x: 5, y: -2 }}
                  className="flex items-center gap-4 p-5 bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-2xl border border-emerald-300 hover:shadow-lg transition-all"
                >
                  <div className="w-14 h-14 bg-white text-emerald-700 rounded-2xl flex items-center justify-center shrink-0 shadow-md border border-emerald-300/30">
                    <Icon3DPartnership size="w-7 h-7" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-emerald-700 dark:text-white text-sm">Mission</h4>
                    <p className="text-xs text-slate-600 dark:text-slate-400">Accelerate digital transformation everywhere.</p>
                  </div>
                </motion.div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="aspect-square bg-blue-50/50 dark:bg-white/5 rounded-3xl overflow-hidden flex items-center justify-center p-8">
                 <img 
                   src="https://illustrations.popsy.co/blue/web-design.svg" 
                   alt="Web Development Illustration" 
                   className="w-full h-full object-contain animate__animated animate__bounceIn" 
                 />
              </div>
              <div className="absolute -bottom-10 -left-10 bg-[#002a5c] p-8 rounded-3xl shadow-xl hidden md:block">
                <div className="flex items-center gap-4">
                  <Award size={40} className="text-[#002a5c]" />
                  <div>
                    <p className="text-2xl font-semibold text-[#ffffff]">Top Rated</p>
                    <p className="text-sm font-medium text-[#ffffff]/70">By 500+ Businesses</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Values - Reusing from Home but in a full section */}
      <section className="py-24 bg-slate-50 dark:bg-[#001a35]">
        <div className="max-w-7xl mx-auto px-4 text-center mb-16">
          <h2 className="text-3xl font-semibold text-[#002a5c] dark:text-white">Why Choose OETechs?</h2>
          <p className="text-slate-500 dark:text-slate-400 mt-4 max-w-2xl mx-auto">We combine technical expertise with a deep understanding of business goals to deliver exceptional results.</p>
        </div>

        <div className="max-w-7xl mx-auto px-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {[
            {
              title: 'Strategic Partnerships',
              desc: 'We work close with our clients and consider them as partners, ensuring a mutual win-win situation.',
              icon: Users,
              color: 'text-blue-600',
              darkColor: 'dark:text-blue-400',
              bg: 'bg-blue-50',
              darkBg: 'dark:bg-blue-900/20'
            },
            {
              title: 'Radical Transparency',
              desc: 'We integrate the client throughout the development phases, providing full clarity and learning opportunities.',
              icon: Eye,
              color: 'text-emerald-600',
              darkColor: 'dark:text-emerald-400',
              bg: 'bg-emerald-50',
              darkBg: 'dark:bg-emerald-900/20'
            },
            {
              title: 'Innovative Thinking',
              desc: 'We think outside the box to come up with proper strategies and solutions within your budget.',
              icon: Lightbulb,
              color: 'text-amber-600',
              darkColor: 'dark:text-amber-400',
              bg: 'bg-amber-50',
              darkBg: 'dark:bg-amber-900/20'
            },
            {
              title: 'Lean Mentality',
              desc: 'We value staying lean, removing unnecessary steps by reusing code and backing decisions with data.',
              icon: Activity,
              color: 'text-indigo-600',
              darkColor: 'dark:text-indigo-400',
              bg: 'bg-indigo-50',
              darkBg: 'dark:bg-indigo-900/20'
            }
          ].map((value, i) => (
            <motion.div 
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="bg-white dark:bg-[#001226] p-8 rounded-3xl shadow-sm hover:shadow-xl transition-all border border-slate-100 dark:border-white/10 group text-center"
            >
              <div className={`w-16 h-16 ${value.bg} ${value.darkBg} ${value.color} ${value.darkColor} rounded-2xl flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform mx-auto mb-6`}>
                {React.createElement(value.icon, { size: 32 })}
              </div>
              <h4 className="font-semibold text-lg text-[#002a5c] dark:text-white mb-3">{value.title}</h4>
              <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">{value.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>
      
      {/* Team Preview CTA */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-4">
           <div className="bg-[#002a5c] rounded-[3rem] p-12 text-center text-white relative overflow-hidden">
             <div className="relative z-10">
               <h2 className="text-3xl font-semibold mb-6">Our Experts are Ready to Help</h2>
               <p className="text-blue-200 mb-8 max-w-xl mx-auto">Get in touch with our team of developers, designers, and strategists to start your digital journey today.</p>
               <Link 
                 to="/team" 
                 className="inline-block px-10 py-4 bg-[#fff] text-[#00ff] rounded-2xl font-semibold hover:scale-105 transition-transform shadow-lg shadow-yellow-500/20"
               >
                 Meet Our Team
               </Link>
             </div>
             <div className="absolute top-0 left-0 w-64 h-64 bg-blue-400/10 rounded-full blur-3xl -ml-32 -mt-32" />
             <div className="absolute bottom-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl -mr-32 -mb-32" />
           </div>
        </div>
      </section>
    </div>
  );
};

export default About;
